import { FolderOpen, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { ShortcutRecorder } from '@/components/ShortcutRecorder'
import { Button } from '@/components/ui/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { usePlatform, useSettings } from '@/store/app-store'
import { ENDPOINT_PROBLEM_MESSAGE, scratchpadEndpoint } from '@/sync/endpoint'
import type { ThemePreference } from '@shared/types'

const THEME_OPTIONS = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

const FONT_SIZE_OPTIONS = [12, 13, 14, 15, 16, 18, 20].map((size) => ({
  value: String(size),
  label: `${size} px`,
}))

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="border-b border-border px-4 py-3 last:border-b-0">
      <h3 className="text-[11px] tracking-wide text-fg-faint uppercase">{title}</h3>
      <div className="mt-1 divide-y divide-border">{children}</div>
    </section>
  )
}

export interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps): ReactNode {
  const platform = usePlatform()
  const { settings, update } = useSettings()

  const { info } = platform
  const desktop = info.kind === 'desktop'
  const mac = info.os === 'darwin'

  // The same function the sync path obeys, so the dialog cannot promise an
  // endpoint that the client will then refuse (ADR-0003).
  const endpoint = scratchpadEndpoint(settings.sync.endpoint)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        description="Preferences for Jot: appearance, editor, shortcut, storage and sync."
        // Escape belongs to the topmost overlay. Without this the panel's own
        // Escape handler would also fire and hide the whole window.
        onEscapeKeyDown={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" aria-label="Close settings">
              <X size={14} strokeWidth={1.5} />
            </Button>
          </DialogClose>
        </DialogHeader>

        <Section title="General">
          <Field label="Theme" htmlFor="settings-theme">
            <Select
              id="settings-theme"
              value={settings.theme}
              onValueChange={(value) => void update({ theme: value as ThemePreference })}
              options={THEME_OPTIONS}
            />
          </Field>

          <Field label="Font size" htmlFor="settings-font-size">
            <Select
              id="settings-font-size"
              value={String(settings.fontSize)}
              onValueChange={(value) => void update({ fontSize: Number(value) })}
              options={FONT_SIZE_OPTIONS}
            />
          </Field>

          <Field label="Line wrap" hint="Soft-wrap long lines." htmlFor="settings-line-wrap">
            <Switch
              id="settings-line-wrap"
              checked={settings.lineWrap}
              onCheckedChange={(checked) => void update({ lineWrap: checked })}
            />
          </Field>

          <Field label="Spell check" htmlFor="settings-spell-check">
            <Switch
              id="settings-spell-check"
              checked={settings.spellCheck}
              onCheckedChange={(checked) => void update({ spellCheck: checked })}
            />
          </Field>
        </Section>

        {desktop ? (
          <Section title="Desktop">
            <Field
              label="Global shortcut"
              hint="Shows and hides the panel."
              htmlFor="settings-shortcut"
            >
              <ShortcutRecorder
                id="settings-shortcut"
                value={settings.shortcut}
                onChange={(shortcut) => void update({ shortcut })}
              />
            </Field>

            <Field
              label="Hide on blur"
              hint="Dismiss the panel when it loses focus."
              htmlFor="settings-hide-on-blur"
            >
              <Switch
                id="settings-hide-on-blur"
                checked={settings.hideOnBlur}
                onCheckedChange={(checked) => void update({ hideOnBlur: checked })}
              />
            </Field>

            <Field label="Launch at login" htmlFor="settings-launch-at-login">
              <Switch
                id="settings-launch-at-login"
                checked={settings.launchAtLogin}
                onCheckedChange={(checked) => void update({ launchAtLogin: checked })}
              />
            </Field>

            {/* The main process only acts on this through `app.dock`, so the row
                stays visible but inert everywhere else. */}
            <Field
              label="Show in Dock"
              hint={mac ? undefined : 'macOS only.'}
              htmlFor="settings-show-in-dock"
            >
              <Switch
                id="settings-show-in-dock"
                disabled={!mac}
                checked={settings.showInDock}
                onCheckedChange={(checked) => void update({ showInDock: checked })}
              />
            </Field>
          </Section>
        ) : null}

        <Section title="Storage">
          <Field label="Location" hint={info.storageLabel}>
            {desktop ? (
              <Button
                aria-label="Open notes folder"
                onClick={() => platform.window.openNotesFolder()}
              >
                <FolderOpen size={14} strokeWidth={1.5} className="text-fg-muted" />
                Open folder
              </Button>
            ) : null}
          </Field>
        </Section>

        <Section title="Sync">
          <Field
            label="Enabled"
            hint="Push this scratchpad to a Jot Server you run yourself."
            htmlFor="settings-sync-enabled"
          >
            <Switch
              id="settings-sync-enabled"
              checked={settings.sync.enabled}
              onCheckedChange={(checked) => void update({ sync: { enabled: checked } })}
            />
          </Field>

          {/* Off is off: with the switch down there is nothing to configure, and
              nothing goes on the wire whatever these fields hold. */}
          {settings.sync.enabled ? (
            <>
              <Field
                label="Server"
                hint="Base address; /scratchpad is appended."
                htmlFor="settings-sync-endpoint"
              >
                <div className="flex flex-col items-end gap-1">
                  <Input
                    id="settings-sync-endpoint"
                    className="w-[210px]"
                    inputMode="url"
                    placeholder="https://jot.example.com"
                    value={settings.sync.endpoint}
                    onChange={(event) =>
                      void update({ sync: { endpoint: event.target.value } })
                    }
                  />
                  {!endpoint.ok ? (
                    <span
                      role="status"
                      className="max-w-[210px] text-right text-[11px] leading-snug text-danger"
                    >
                      {ENDPOINT_PROBLEM_MESSAGE[endpoint.problem]}
                    </span>
                  ) : null}
                </div>
              </Field>

              <Field
                label="Token"
                hint="The server prints it once. It is stored on this device."
                htmlFor="settings-sync-token"
              >
                <Input
                  id="settings-sync-token"
                  // Masked because the panel is a floating window that opens over
                  // whatever else is on screen, including a shared one.
                  type="password"
                  className="w-[210px]"
                  placeholder="Bearer token"
                  value={settings.sync.token}
                  onChange={(event) => void update({ sync: { token: event.target.value } })}
                />
              </Field>

              <Field
                label="This device"
                hint={`Named in a conflict as the device that pushed. Defaults to "${info.deviceLabel}".`}
                htmlFor="settings-sync-device-label"
              >
                <Input
                  id="settings-sync-device-label"
                  className="w-[210px]"
                  placeholder={info.deviceLabel}
                  value={settings.sync.deviceLabel}
                  onChange={(event) =>
                    void update({ sync: { deviceLabel: event.target.value } })
                  }
                />
              </Field>
            </>
          ) : null}
        </Section>

        <Section title="About">
          {/* One child, so the section's row dividers do not split the block. */}
          <div className="py-2">
            <div className="flex items-baseline gap-2 text-[12px] text-fg">
              <span>Jot</span>
              <span className="text-fg-muted">{info.appVersion}</span>
            </div>
            {desktop ? (
              <div className="mt-1.5 text-[11px] leading-relaxed text-fg-faint">
                <div>Electron {info.electronVersion ?? 'unknown'}</div>
                <div>Chrome {info.chromeVersion ?? 'unknown'}</div>
                <div>Node {info.nodeVersion ?? 'unknown'}</div>
              </div>
            ) : null}
          </div>
        </Section>
      </DialogContent>
    </Dialog>
  )
}
